import { useState, useEffect, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useAuthStore, hospitals, hospitalDoctors } from '@/stores/authStore'
import { usePatientStore } from '@/stores/patientStore'
import PatientVoiceInput from '@/components/PatientVoiceInput'

// 病名リスト
const DISEASE_OPTIONS = [
  '潰瘍性大腸炎',
  '自閉スペクトラム症',
  '過敏性腸症候群',
  'アトピー・アレルギー',
  '便秘症',
  'うつ病',
  'クローン病',
  '認知症',
  'パーキンソン病',
  '肥満症',
  '糖尿病',
  '慢性疲労症候群',
]

export default function PatientEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getPatient, updatePatient } = usePatientStore()
  const isAdminOrOffice = user?.role === 'admin' || user?.role === 'office'

  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    gender: '',
    birthDate: '',
    diseaseNames: [] as string[],
    customDisease: '',
    diseaseDetail: '',
    treatment: '',
    primaryHospitalId: '',
    primaryDoctorId: '',
    transplantHospitalId: '',
    transplantDoctorId: '',
  })

  const [primaryDoctors, setPrimaryDoctors] = useState<Array<{ id: string; name: string; specialty?: string }>>([])
  const [transplantDoctors, setTransplantDoctors] = useState<Array<{ id: string; name: string; specialty?: string }>>([])
  const [loading, setLoading] = useState(true)

  // 既存データを読み込み
  useEffect(() => {
    if (id) {
      const patient = getPatient(id)
      if (patient) {
        // 病名を配列に変換（複数病名がカンマ区切りの場合）
        const existingDiseases = patient.diseaseName ? patient.diseaseName.split('、') : []
        const knownDiseases = existingDiseases.filter(d => DISEASE_OPTIONS.includes(d))
        const customDiseases = existingDiseases.filter(d => !DISEASE_OPTIONS.includes(d))

        setFormData({
          lastName: patient.lastName || '',
          firstName: patient.firstName || '',
          gender: patient.gender === '男性' ? 'male' : patient.gender === '女性' ? 'female' : 'other',
          birthDate: patient.birthDate || '',
          diseaseNames: knownDiseases,
          customDisease: customDiseases.join('、'),
          diseaseDetail: patient.diseaseDetail || '',
          treatment: patient.treatment || '',
          primaryHospitalId: patient.primaryHospitalId || '',
          primaryDoctorId: patient.primaryDoctorId || '',
          transplantHospitalId: patient.transplantHospitalId || '',
          transplantDoctorId: patient.transplantDoctorId || '',
        })
      }
      setLoading(false)
    }
  }, [id, getPatient])

  // 主治医病院の変更時に医師リストを更新
  useEffect(() => {
    if (formData.primaryHospitalId) {
      setPrimaryDoctors(hospitalDoctors[formData.primaryHospitalId] || [])
    } else {
      setPrimaryDoctors([])
    }
  }, [formData.primaryHospitalId])

  // 移植担当病院の変更時に医師リストを更新
  useEffect(() => {
    if (formData.transplantHospitalId) {
      setTransplantDoctors(hospitalDoctors[formData.transplantHospitalId] || [])
    } else {
      setTransplantDoctors([])
    }
  }, [formData.transplantHospitalId])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // 病名を結合（選択された病名 + カスタム病名）
    const allDiseases = [...formData.diseaseNames]
    if (formData.customDisease.trim()) {
      allDiseases.push(...formData.customDisease.split('、').map(d => d.trim()).filter(d => d))
    }

    // 病院・医師の名前を取得
    const primaryHospital = hospitals.find(h => h.id === formData.primaryHospitalId)
    const primaryDoctor = primaryDoctors.find(d => d.id === formData.primaryDoctorId)
    const transplantHospital = hospitals.find(h => h.id === formData.transplantHospitalId)
    const transplantDoctor = transplantDoctors.find(d => d.id === formData.transplantDoctorId)

    // 年齢を計算
    const birthDate = new Date(formData.birthDate)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const m = today.getMonth() - birthDate.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }

    if (id) {
      updatePatient(id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: `${formData.lastName}${formData.firstName}`,
        gender: formData.gender === 'male' ? '男性' : formData.gender === 'female' ? '女性' : 'その他',
        age: age || 0,
        birthDate: formData.birthDate,
        diseaseName: allDiseases.join('、'),
        diseaseDetail: formData.diseaseDetail,
        treatment: formData.treatment,
        primaryHospitalId: formData.primaryHospitalId,
        primaryHospitalName: primaryHospital?.name || '',
        transplantHospitalId: formData.transplantHospitalId || formData.primaryHospitalId,
        transplantHospitalName: transplantHospital?.name || primaryHospital?.name || '',
        primaryDoctorId: formData.primaryDoctorId,
        primaryDoctorName: primaryDoctor?.name || '',
        transplantDoctorId: formData.transplantDoctorId || formData.primaryDoctorId,
        transplantDoctorName: transplantDoctor?.name || primaryDoctor?.name || '',
      })
    }

    navigate(`/patients/${id}`)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  // 病名チェックボックスの変更
  const handleDiseaseChange = (disease: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      diseaseNames: checked
        ? [...prev.diseaseNames, disease]
        : prev.diseaseNames.filter(d => d !== disease),
    }))
  }

  // 音声入力からの更新
  const handleVoiceFormUpdate = useCallback((data: Partial<{
    lastName: string
    firstName: string
    gender: string
    birthDate: string
    diseaseName: string
    diseaseDetail: string
    treatment: string
  }>) => {
    setFormData(prev => {
      const newData = { ...prev }
      
      if (data.lastName) newData.lastName = data.lastName
      if (data.firstName) newData.firstName = data.firstName
      if (data.gender) newData.gender = data.gender
      if (data.birthDate) newData.birthDate = data.birthDate
      if (data.diseaseDetail) newData.diseaseDetail = data.diseaseDetail
      if (data.treatment) newData.treatment = data.treatment
      
      // 病名の処理（複数対応）
      if (data.diseaseName) {
        const diseases = data.diseaseName.split('、').map(d => d.trim())
        diseases.forEach(disease => {
          if (DISEASE_OPTIONS.includes(disease)) {
            if (!newData.diseaseNames.includes(disease)) {
              newData.diseaseNames = [...newData.diseaseNames, disease]
            }
          } else if (disease && !newData.customDisease.includes(disease)) {
            newData.customDisease = newData.customDisease
              ? `${newData.customDisease}、${disease}`
              : disease
          }
        })
      }
      
      return newData
    })
  }, [])

  const availableHospitals = isAdminOrOffice ? hospitals : hospitals.filter(h => h.id === user?.hospitalId)

  if (loading) {
    return <div className="loading">読み込み中...</div>
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <Link to={`/patients/${id}`} className="aip-button aip-button--icon">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <h1 className="aip-main__header-title">患者カルテ編集</h1>
        </div>
      </header>

      <div className="aip-main__content">
        <form onSubmit={handleSubmit} className="form-container">
          <div className="form-header">
            <h2 className="form-title">患者情報を編集</h2>
            <p className="form-description">
              患者の情報を更新し、保存ボタンで反映します。
            </p>
          </div>

          <div className="form-body">
            {/* 音声入力セクション */}
            <section className="form-section">
              <h3 className="form-section__title">
                <span className="material-symbols-outlined">mic</span>
                音声入力
              </h3>
              <PatientVoiceInput
                onFormUpdate={handleVoiceFormUpdate}
                autoStart={false}
              />
            </section>

            {/* 基本情報セクション */}
            <section className="form-section">
              <h3 className="form-section__title">基本情報</h3>
              <div className="form-grid">
                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="lastName">
                    氏名（姓）<span className="required">※必須</span>
                  </label>
                  <input
                    className="aip-floating-field__control aip-input-text"
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="例：山田"
                    required
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="firstName">
                    氏名（名）<span className="required">※必須</span>
                  </label>
                  <input
                    className="aip-floating-field__control aip-input-text"
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="例：太郎"
                    required
                  />
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="gender">
                    性別<span className="required">※必須</span>
                  </label>
                  <select
                    className="aip-floating-field__control aip-select"
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">選択してください</option>
                    <option value="male">男性</option>
                    <option value="female">女性</option>
                    <option value="other">その他</option>
                  </select>
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="birthDate">
                    生年月日
                  </label>
                  <input
                    className="aip-floating-field__control aip-input-text"
                    type="date"
                    id="birthDate"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </section>

            {/* 病名・治療セクション */}
            <section className="form-section">
              <h3 className="form-section__title">病名・治療</h3>
              
              {/* 病名の複数選択 */}
              <div className="disease-selection">
                <label className="aip-floating-field__label">
                  病名（複数選択可）<span className="required">※必須</span>
                </label>
                <div className="disease-checkboxes">
                  {DISEASE_OPTIONS.map((disease) => (
                    <label key={disease} className="disease-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.diseaseNames.includes(disease)}
                        onChange={(e) => handleDiseaseChange(disease, e.target.checked)}
                      />
                      <span className="disease-checkbox__label">{disease}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 選択された病名の表示 */}
              {formData.diseaseNames.length > 0 && (
                <div className="selected-diseases">
                  <label className="aip-floating-field__label">選択された病名</label>
                  <div className="disease-tags">
                    {formData.diseaseNames.map((disease) => (
                      <span key={disease} className="disease-tag-selected">
                        {disease}
                        <button
                          type="button"
                          onClick={() => handleDiseaseChange(disease, false)}
                          className="disease-tag-remove"
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* カスタム病名 */}
              <div className="form-grid" style={{ marginTop: 'var(--spacing-md)' }}>
                <div className="aip-floating-field aip-floating-field--full">
                  <label className="aip-floating-field__label" htmlFor="customDisease">
                    その他の病名（自由記載）
                  </label>
                  <input
                    className="aip-floating-field__control aip-input-text"
                    type="text"
                    id="customDisease"
                    name="customDisease"
                    value={formData.customDisease}
                    onChange={handleChange}
                    placeholder="リストにない病名を入力（複数の場合は読点「、」で区切る）"
                  />
                </div>

                <div className="aip-floating-field aip-floating-field--full">
                  <label className="aip-floating-field__label" htmlFor="diseaseDetail">
                    病名詳細
                  </label>
                  <textarea
                    className="aip-floating-field__control aip-textarea"
                    id="diseaseDetail"
                    name="diseaseDetail"
                    value={formData.diseaseDetail}
                    onChange={handleChange}
                    rows={3}
                    placeholder="病名の詳細を入力してください"
                  />
                </div>

                <div className="aip-floating-field aip-floating-field--full">
                  <label className="aip-floating-field__label" htmlFor="treatment">
                    治療内容
                  </label>
                  <textarea
                    className="aip-floating-field__control aip-textarea"
                    id="treatment"
                    name="treatment"
                    value={formData.treatment}
                    onChange={handleChange}
                    rows={3}
                    placeholder="治療内容を入力してください"
                  />
                </div>
              </div>
            </section>

            {/* 主治医セクション */}
            <section className="form-section">
              <h3 className="form-section__title">
                <span className="material-symbols-outlined">person</span>
                主治医（プライマリケア）
              </h3>
              <div className="form-grid">
                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="primaryHospitalId">
                    医療機関<span className="required">※必須</span>
                  </label>
                  <select
                    className="aip-floating-field__control aip-select"
                    id="primaryHospitalId"
                    name="primaryHospitalId"
                    value={formData.primaryHospitalId}
                    onChange={handleChange}
                    required
                    disabled={!isAdminOrOffice}
                  >
                    <option value="">選択してください</option>
                    {availableHospitals.map((hospital) => (
                      <option key={hospital.id} value={hospital.id}>
                        {hospital.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="primaryDoctorId">
                    担当医師<span className="required">※必須</span>
                  </label>
                  <select
                    className="aip-floating-field__control aip-select"
                    id="primaryDoctorId"
                    name="primaryDoctorId"
                    value={formData.primaryDoctorId}
                    onChange={handleChange}
                    required
                    disabled={!formData.primaryHospitalId}
                  >
                    <option value="">
                      {formData.primaryHospitalId ? '医師を選択' : '先に医療機関を選択'}
                    </option>
                    {primaryDoctors.map((doctor) => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.name} {doctor.specialty && `（${doctor.specialty}）`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            {/* 移植担当医セクション */}
            <section className="form-section">
              <h3 className="form-section__title">
                <span className="material-symbols-outlined">vaccines</span>
                移植担当医
              </h3>
              <div className="form-grid">
                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="transplantHospitalId">
                    医療機関
                  </label>
                  <select
                    className="aip-floating-field__control aip-select"
                    id="transplantHospitalId"
                    name="transplantHospitalId"
                    value={formData.transplantHospitalId}
                    onChange={handleChange}
                  >
                    <option value="">選択してください（任意）</option>
                    {hospitals.map((hospital) => (
                      <option key={hospital.id} value={hospital.id}>
                        {hospital.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="aip-floating-field">
                  <label className="aip-floating-field__label" htmlFor="transplantDoctorId">
                    担当医師
                  </label>
                  <select
                    className="aip-floating-field__control aip-select"
                    id="transplantDoctorId"
                    name="transplantDoctorId"
                    value={formData.transplantDoctorId}
                    onChange={handleChange}
                    disabled={!formData.transplantHospitalId}
                  >
                    <option value="">
                      {formData.transplantHospitalId ? '医師を選択（任意）' : '先に医療機関を選択'}
                    </option>
                    {transplantDoctors.map((doctor) => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.name} {doctor.specialty && `（${doctor.specialty}）`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="form-hint">
                ※ 移植担当医は主治医と異なる場合のみ設定してください
              </p>
            </section>
          </div>

          <div className="form-footer">
            <Link to={`/patients/${id}`} className="aip-button aip-button--outline">
              キャンセル
            </Link>
            <button type="submit" className="aip-button aip-button--solid-fill">
              保存
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .form-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .form-header {
          padding: var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }

        .form-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: var(--spacing-xs);
        }

        .form-description {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .form-body {
          padding: var(--spacing-lg);
        }

        .form-section {
          margin-bottom: var(--spacing-xl);
        }

        .form-section:last-child {
          margin-bottom: 0;
        }

        .form-section__title {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--border-color);
        }

        .form-section__title .material-symbols-outlined {
          font-size: 20px;
          color: var(--color-blue600);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-md);
        }

        .aip-floating-field--full {
          grid-column: 1 / -1;
        }

        .aip-floating-field__label {
          display: block;
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-xs);
        }

        .aip-input-text,
        .aip-select,
        .aip-textarea {
          width: 100%;
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: white;
        }

        .aip-input-text:focus,
        .aip-select:focus,
        .aip-textarea:focus {
          outline: none;
          border-color: var(--color-blue500);
          box-shadow: 0 0 0 3px var(--color-blue100);
        }

        .aip-select:disabled {
          background-color: var(--color-solid-gray50);
          cursor: not-allowed;
        }

        .aip-textarea {
          resize: vertical;
        }

        .required {
          color: var(--color-alert);
          font-size: 0.75rem;
          margin-left: var(--spacing-xs);
        }

        .form-hint {
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin-top: var(--spacing-sm);
        }

        .form-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-sm);
          padding: var(--spacing-lg);
          border-top: 1px solid var(--border-color);
        }

        /* 病名選択 */
        .disease-selection {
          margin-bottom: var(--spacing-md);
        }

        .disease-checkboxes {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          background-color: var(--color-solid-gray25);
          border-radius: var(--border-radius-md);
          border: 1px solid var(--border-color);
        }

        .disease-checkbox {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          cursor: pointer;
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--border-radius-sm);
          transition: background-color 0.2s;
        }

        .disease-checkbox:hover {
          background-color: var(--color-blue50);
        }

        .disease-checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: var(--color-blue500);
          cursor: pointer;
        }

        .disease-checkbox__label {
          font-size: 0.875rem;
          color: var(--text-primary);
        }

        /* 選択された病名 */
        .selected-diseases {
          margin-top: var(--spacing-md);
        }

        .disease-tags {
          display: flex;
          flex-wrap: wrap;
          gap: var(--spacing-xs);
        }

        .disease-tag-selected {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-xs) var(--spacing-sm);
          background-color: var(--color-blue100);
          color: var(--color-blue700);
          border-radius: var(--border-radius-md);
          font-size: 0.8125rem;
          font-weight: 500;
        }

        .disease-tag-remove {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          padding: 0;
          border: none;
          background: none;
          color: var(--color-blue600);
          cursor: pointer;
          border-radius: 50%;
          transition: background-color 0.2s;
        }

        .disease-tag-remove:hover {
          background-color: var(--color-blue200);
        }

        .disease-tag-remove .material-symbols-outlined {
          font-size: 14px;
        }

        .loading {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: var(--text-secondary);
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }

          .disease-checkboxes {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </>
  )
}
